// Client for api/db/cyber-bytes/*. URLs need the trailing slash (buildApiUrl(..., true)).
import { buildApiUrl, getApiHeaders } from '../queriesHelpers';
import type {
  CyberBytesId,
  Module,
  ModuleType,
  Section,
  Series,
  SeriesDetail,
} from '../../components/CyberBytes/types';

const BASE = 'db/cyber-bytes';

type Id = CyberBytesId | string;


export interface SeriesListResponse {
  series: Series[];
  total: number;
  averageRating: number;
}

export interface SeriesListParams {
  search?: string;
  category?: string | null;
  limit?: number;
  offset?: number;
}

export interface CreateSeriesBody {
  title: string;
  description?: string;
  category?: string;
  tags?: string[];
}

export type UpdateSeriesBody = Partial<CreateSeriesBody> & { published?: boolean };

export interface SectionBody {
  title?: string;
  description?: string;
}

export interface ModuleCreateBody {
  title: string;
  description?: string;
  type: ModuleType;
  content: Record<string, any>;
  required?: boolean;
  section?: CyberBytesId | null;
}

export type ModuleUpdateBody = Partial<Omit<ModuleCreateBody, 'type'>>;

export interface PresignImageResponse {
  upload_url: string;
  object_key: string;
  content_type: string;
  expires_in: number;
}

export interface ImageUrlResponse {
  url: string;
  expires_in: number | null;
  refresh_after?: number;
}

export interface RateResponse {
  id: CyberBytesId;
  rating: number;
  review?: string;
}


export const cyberBytesErrorMessage = (payload: any, status: number, fallback: string): string => {
  const pick = (value: any): string | null => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.length ? pick(value[0]) : null;
    if (typeof value === 'object') {
      for (const key of Object.keys(value)) {
        const found = pick(value[key]);
        if (found) return found;
      }
    }
    return null;
  };

  const message = pick(payload?.detail) ?? pick(payload?.error) ?? pick(payload?.message) ?? pick(payload);

  return message || `${fallback} (${status})`;
};

export class CyberBytesApiError extends Error {
  status: number;

  payload: any;

  constructor(message: string, status: number, payload: any) {
    super(message);
    this.name = 'CyberBytesApiError';
    this.status = status;
    this.payload = payload;
  }
}

async function cyberBytesFetch<T = any>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  options: { params?: Record<string, any>; body?: any; fallback?: string } = {},
): Promise<T> {
  const { params = {}, body, fallback = 'Request failed' } = options;
  const endpoint = `${BASE}/${path.replace(/^\/+/, '')}`;

  const response = await fetch(buildApiUrl(endpoint, params, true), {
    method,
    headers: { ...getApiHeaders(null, method, endpoint) } as Record<string, string>,
    credentials: 'include',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  const text = await response.text();

  let parsed: any = null;

  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }

  if (!response.ok) {
    throw new CyberBytesApiError(
      cyberBytesErrorMessage(parsed, response.status, fallback),
      response.status,
      parsed,
    );
  }

  return parsed as T;
}


export const listSeries = (params: SeriesListParams = {}) => {
  const query: Record<string, any> = {};

  if (params.search) query.search = params.search;
  if (params.category) query.category = params.category;
  if (params.limit !== undefined) query.limit = params.limit;
  if (params.offset !== undefined) query.offset = params.offset;

  return cyberBytesFetch<SeriesListResponse>('GET', 'series', {
    params: query,
    fallback: 'Failed to load series',
  });
};

export const getSeriesDetail = (seriesId: Id) =>
  cyberBytesFetch<SeriesDetail>('GET', `series/${seriesId}`, { fallback: 'Failed to load series' });

export const createSeries = (body: CreateSeriesBody) =>
  cyberBytesFetch<Series>('POST', 'series', { body, fallback: 'Failed to create series' });

export const updateSeries = (seriesId: Id, body: UpdateSeriesBody) =>
  cyberBytesFetch<Series>('PATCH', `series/${seriesId}`, { body, fallback: 'Failed to save series' });

export const deleteSeries = (seriesId: Id) =>
  cyberBytesFetch<null>('DELETE', `series/${seriesId}`, { fallback: 'Failed to delete series' });

export const createSection = (seriesId: Id, body: SectionBody) =>
  cyberBytesFetch<Section>('POST', `series/${seriesId}/sections`, {
    body,
    fallback: 'Failed to save section',
  });

export const updateSection = (seriesId: Id, sectionId: Id, body: SectionBody) =>
  cyberBytesFetch<Section>('PATCH', `series/${seriesId}/sections/${sectionId}`, {
    body,
    fallback: 'Failed to save section',
  });

export const deleteSection = (seriesId: Id, sectionId: Id) =>
  cyberBytesFetch<null>('DELETE', `series/${seriesId}/sections/${sectionId}`, {
    fallback: 'Failed to delete section',
  });

export const reorderSections = (seriesId: Id, sectionIds: Id[]) =>
  cyberBytesFetch<Section[]>('POST', `series/${seriesId}/sections/reorder`, {
    body: { sectionIds },
    fallback: 'Failed to reorder sections',
  });

export const createModule = (seriesId: Id, body: ModuleCreateBody) =>
  cyberBytesFetch<Module>('POST', `series/${seriesId}/modules`, {
    body,
    fallback: 'Failed to add module',
  });

export const updateModule = (seriesId: Id, moduleId: Id, body: ModuleUpdateBody) =>
  cyberBytesFetch<Module>('PATCH', `series/${seriesId}/modules/${moduleId}`, {
    body,
    fallback: 'Failed to update module',
  });

export const deleteModule = (seriesId: Id, moduleId: Id) =>
  cyberBytesFetch<null>('DELETE', `series/${seriesId}/modules/${moduleId}`, {
    fallback: 'Failed to delete module',
  });

export const reorderModules = (seriesId: Id, moduleIds: Id[]) =>
  cyberBytesFetch<Module[]>('POST', `series/${seriesId}/modules/reorder`, {
    body: { moduleIds },
    fallback: 'Failed to reorder modules',
  });

export const presignModuleImage = (seriesId: Id, file: File) =>
  cyberBytesFetch<PresignImageResponse>('POST', `series/${seriesId}/modules/presign-image`, {
    body: { filename: file.name, content_type: file.type || 'application/octet-stream' },
    fallback: "Image upload isn't available yet",
  });

/** Resolves to the object key to store on the module. */
export async function uploadModuleImage(seriesId: Id, file: File): Promise<string> {
  const presign = await presignModuleImage(seriesId, file);

  const putRes = await fetch(presign.upload_url, {
    method: 'PUT',
    headers: { 'Content-Type': presign.content_type || file.type || 'application/octet-stream' },
    body: file,
  });

  if (!putRes.ok) throw new Error('Failed to upload image');

  return presign.object_key;
}

export const getModuleImageUrl = (seriesId: Id, moduleId: Id) =>
  cyberBytesFetch<ImageUrlResponse>('GET', `series/${seriesId}/modules/${moduleId}/image-url`, {
    fallback: 'Failed to load image',
  });

export const markModuleViewed = (seriesId: Id, moduleId: Id) =>
  cyberBytesFetch<any>('POST', `series/${seriesId}/modules/${moduleId}/viewed`, {
    fallback: 'Failed to mark module as viewed',
  });

export const completeSeries = (seriesId: Id) =>
  cyberBytesFetch<{ completed: boolean; completedAt: string }>('POST', `series/${seriesId}/complete`, {
    fallback: 'All required modules must be viewed before completing.',
  });

export const rateSeries = (seriesId: Id, rating: number, review?: string) =>
  cyberBytesFetch<RateResponse>('POST', `series/${seriesId}/rate`, {
    body: { rating, review: review || undefined },
    fallback: 'Failed to submit rating',
  });
